<script setup>
import { ref, watch, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import CouriersAPI from 'dashboard/api/additional_couriers_info';
import ContactInfoRow from './ContactInfoRow.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SimpleDivider from '../../../../../v3/components/Divider/SimpleDivider.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  additionalAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();

const nextUpdateIn = computed(() => store.state.couriers.nextUpdateIn);

const courierInfo = ref(null);
const isLoading = ref(false);

const loadAdditionalCourierInfo = async () => {
  if (!props.contactId) return;

  isLoading.value = true;
  try {
    const response = await CouriersAPI.get(props.contactId);
    courierInfo.value = response.data;
  } catch (error) {
    courierInfo.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.contactId,
  (newId, oldId) => {
    if (newId !== oldId) {
      courierInfo.value = null;
    }
    if (newId) {
      loadAdditionalCourierInfo();
    }
  },
  { immediate: true }
);

watch(
  () => store.state.couriers.version,
  () => {
    loadAdditionalCourierInfo();
  }
);
</script>

<template>
  <div
    v-if="isLoading"
    class="flex items-center gap-2 justify-center p-6 text-n-slate-12"
  >
    <span class="text-sm">{{
      $t('CONTACT_PANEL.ADDITIONAL_COURIER_INFO.LOADING')
    }}</span>
    <Spinner class="size-10" />
  </div>
  <div v-else>
    <div v-if="courierInfo" class="flex flex-col items-start w-full gap-2">
      <ContactInfoRow
        :value="`${courierInfo.sheets_name || ''}`"
        :title="$t('COURIER_PROFILE_ROWS.NAME')"
        icon="person-account"
        emoji="👤"
        show-copy
        multiline
      />
      <ContactInfoRow
        :value="`${courierInfo.sheets_email || ''}`"
        :title="$t('COURIER_PROFILE_ROWS.EMAIL')"
        icon="mail"
        emoji="✉️"
        show-copy
      />
      <ContactInfoRow
        :value="`${courierInfo.sheets_phone || ''}`"
        :title="$t('COURIER_PROFILE_ROWS.PHONE')"
        icon="call"
        emoji="📞"
        show-copy
      />
      <ContactInfoRow
        :value="`${courierInfo.sheets_bank_account || ''}`"
        :title="$t('COURIER_PROFILE_ROWS.BANK_ACCOUNT')"
        icon="building-bank"
        emoji="🏦"
        show-copy
      />
      <ContactInfoRow
        :value="`${courierInfo.sheets_payment_type || ''}`"
        :title="$t('COURIER_PROFILE_ROWS.PAYMENT_TYPE')"
        icon="credit-card-person"
        emoji="💳"
        multiline
      />
      <ContactInfoRow
        :value="`${courierInfo.sheets_city || ''}`"
        :title="$t('COURIER_PROFILE_ROWS.CITY')"
        icon="map"
        emoji="🌍"
      />
      <ContactInfoRow
        :value="`${courierInfo.sheets_status || ''}`"
        :title="$t('COURIER_PROFILE_ROWS.CONTRACT_TYPE')"
        icon="document"
        emoji="📄"
        multiline
      />
      <ContactInfoRow
        :value="`${courierInfo.sheets_zus_hold || ''}`"
        :title="$t('COURIER_PROFILE_ROWS.ZUS_HOLD')"
        icon="number-symbol"
        emoji="📉"
        show-copy
      />
      <ContactInfoRow
        :value="`${courierInfo.sheets_loan || ''}`"
        :title="$t('COURIER_PROFILE_ROWS.LOAN')"
        icon="subtract"
        emoji="💰"
        show-copy
      />
      <ContactInfoRow
        :value="`${courierInfo.sheets_bonus || ''}`"
        :title="$t('COURIER_PROFILE_ROWS.BONUS')"
        icon="plus-sign"
        emoji="🎁"
        show-copy
      />
      <ContactInfoRow
        :value="`${courierInfo.sheets_invite || ''}`"
        :title="$t('COURIER_PROFILE_ROWS.INVITE')"
        icon="person-add"
        emoji="🤝"
        multiline
      />

      <div
        v-if="nextUpdateIn"
        class="next-refresh text-xs text-slate-400 mt-2 px-4"
      >
        {{
          $t('CONTACT_PANEL.ADDITIONAL_COURIER_INFO.NXT_UPDATE', {
            secs: nextUpdateIn,
          })
        }}
      </div>
    </div>
    <p
      v-else
      class="p-3 flex flex-col items-center text-center justify-center h-full"
    >
      {{ $t('CONTACT_PANEL.ADDITIONAL_COURIER_INFO.NO_DATA') }}
    </p>
    <SimpleDivider class="border-t border-slate-200 dark:border-slate-800" />
    <ContactInfoRow
      :value="`${additionalAttributes.social_telegram_user_id || ''}`"
      :title="$t('COURIER_PROFILE_ROWS.TELEGRAM_ID')"
      icon="contact-identify"
      emoji="🆔"
      show-copy
    />
  </div>
</template>
